import type {
  Product,
  ProductAttribute,
  ProductVariant,
} from "../interfaces/catalogProduct";
import {
  findCatalogProductBySlug,
  getCatalogProductSlug,
  resolveCatalogProductPricing,
} from "./catalogProductAdapter";

export type CatalogEntry = {
  parent: Product;
  variant?: ProductVariant;
};

export type CatalogIndex = {
  parents: Product[];
  byUuid: Map<string, CatalogEntry>;
};

export type RouteResolveResult = {
  parent: Product;
  variant?: ProductVariant;
  initialAttributes?: Record<string, string>;
};

export function hasVariants(product: Product): boolean {
  return (product.variants?.length ?? 0) > 0;
}

export function buildCatalogIndex(products: Product[]): CatalogIndex {
  const parents = products;
  const byUuid = new Map<string, CatalogEntry>();

  for (const parent of parents) {
    byUuid.set(parent.productUuid, { parent });
  }

  for (const parent of parents) {
    for (const variant of parent.variants ?? []) {
      // A variant can point at a canonical top-level catalog product. Preserve
      // that product's pricing and adjustments when the UUID is shared.
      if (!byUuid.has(variant.productUuid)) {
        byUuid.set(variant.productUuid, { parent, variant });
      }
    }
  }

  return { parents, byUuid };
}

export function getCatalogEntry(
  index: CatalogIndex,
  productUuid: string
): CatalogEntry | undefined {
  return index.byUuid.get(productUuid);
}

export function variantMatchesSelection(
  variant: ProductVariant,
  selection: Record<string, string>
): boolean {
  const keys = Object.keys(selection).filter((k) => selection[k] !== "");
  if (keys.length === 0) {
    return false;
  }
  return keys.every((key) => variant.attributes[key] === selection[key]);
}

export function findCatalogProductByUuid(
  products: Product[],
  productUuid: string
): Product | undefined {
  return products.find((p) => p.productUuid === productUuid);
}

/**
 * Product shown on the detail page (name, images, description).
 * When attribute selection matches a variant, prefer the root catalog row with that uuid.
 */
export function resolveDisplayCatalogProduct(
  parent: Product,
  products: Product[],
  selection?: Record<string, string>
): Product {
  const variant = selection ? resolveVariant(parent, selection) : undefined;
  if (!variant) {
    return parent;
  }
  return findCatalogProductByUuid(products, variant.productUuid) ?? parent;
}

export function resolveVariant(
  parent: Product,
  selection: Record<string, string>
): ProductVariant | undefined {
  const variants = parent.variants ?? [];
  if (!variants.length) {
    return undefined;
  }

  const attributeIds = (parent.productAttributes ?? []).map((a) => a.id);
  const allSelected =
    attributeIds.length > 0 &&
    attributeIds.every((id) => {
      const v = selection[id];
      return typeof v === "string" && v !== "";
    });

  if (!allSelected) {
    return undefined;
  }

  return variants.find((v) => variantMatchesSelection(v, selection));
}

export function getSelectableValues(
  parent: Product,
  partialSelection: Record<string, string>,
  attributeId: string
): string[] {
  const attr = parent.productAttributes?.find((a) => a.id === attributeId);
  if (!attr) {
    return [];
  }

  const variants = parent.variants ?? [];
  const possible = new Set<string>();

  for (const variant of variants) {
    const matchesOther = Object.entries(partialSelection).every(
      ([key, value]) => {
        if (key === attributeId || value === "") {
          return true;
        }
        return variant.attributes[key] === value;
      }
    );
    if (!matchesOther) {
      continue;
    }
    const value = variant.attributes[attributeId];
    if (value) {
      possible.add(value);
    }
  }

  return attr.values.filter((v) => possible.has(v));
}

export function getVariantPriceRange(
  parent: Product
): { minPrice: number; maxPrice: number } | null {
  const variants = parent.variants ?? [];
  if (!variants.length) {
    return null;
  }
  const prices = variants.map((v) => v.price);
  return {
    minPrice: Math.min(...prices),
    maxPrice: Math.max(...prices),
  };
}

export function resolveProductFromRouteParam(
  param: string | undefined,
  products: Product[]
): RouteResolveResult | null {
  if (!param?.trim()) {
    return null;
  }
  const normalized = param.trim();
  const index = buildCatalogIndex(products);

  const byUuid = index.byUuid.get(normalized);
  if (byUuid) {
    if (byUuid.variant) {
      return {
        parent: byUuid.parent,
        variant: byUuid.variant,
        initialAttributes: { ...byUuid.variant.attributes },
      };
    }
    return { parent: byUuid.parent };
  }

  const bySlug = findCatalogProductBySlug(products, normalized);
  if (bySlug) {
    return { parent: bySlug };
  }

  return null;
}

export function getSellableProductUuid(
  parent: Product,
  selection?: Record<string, string>
): string | undefined {
  if (!hasVariants(parent)) {
    return parent.productUuid;
  }
  if (!selection) {
    return undefined;
  }
  const variant = resolveVariant(parent, selection);
  return variant?.productUuid;
}

export type EffectivePrice = {
  price: number;
  priceRegular: number;
  adjustmentAmount: number;
};

export function getEffectivePrice(
  entry: CatalogEntry,
  at: Date = new Date()
): EffectivePrice {
  if (entry.variant) {
    const price = entry.variant.price;
    return {
      price,
      priceRegular: price,
      adjustmentAmount: 0,
    };
  }

  const resolved = resolveCatalogProductPricing(entry.parent, at);
  return {
    price: resolved.price_total,
    priceRegular: Number(entry.parent.priceRegular) || 0,
    adjustmentAmount: resolved.adjustment_amount,
  };
}

export function filterProductsBySearchTerm(
  products: Product[],
  locale: string,
  searchTerm: string
): Product[] {
  const term = searchTerm.trim().toLowerCase();
  if (!term) {
    return products;
  }

  return products.filter((product) => {
    const translation =
      product.translations[locale as keyof typeof product.translations] ??
      product.translations.et ??
      product.translations.en;
    const parts = [
      translation?.name,
      translation?.displayName,
      translation?.shortDescription,
      translation?.description,
      getCatalogProductSlug(product, locale),
    ];
    if (product.productCodes?.length) {
      for (const code of product.productCodes) {
        parts.push(code.name, code.value);
      }
    }
    return parts.some(
      (part) => typeof part === "string" && part.toLowerCase().includes(term)
    );
  });
}

export function buildCatalogProductDetailHref(
  baseHref: string,
  product: Product,
  locale: string
): string {
  const routeSegment = getCatalogProductSlug(product, locale) ?? product.productUuid;
  return `${baseHref}${routeSegment}`;
}

export function getAttributeName(
  attribute: ProductAttribute,
  locale: string
): string {
  return (
    attribute.translations[locale]?.name ??
    attribute.translations.et?.name ??
    attribute.id
  );
}

export function getAttributeValueLabel(
  attribute: ProductAttribute,
  locale: string,
  valueKey: string
): string {
  return (
    attribute.translations[locale]?.values[valueKey] ??
    attribute.translations.et?.values[valueKey] ??
    valueKey
  );
}

export function emptyAttributeSelection(
  product: Product
): Record<string, string> {
  const selection: Record<string, string> = {};
  for (const attr of product.productAttributes ?? []) {
    selection[attr.id] = "";
  }
  return selection;
}
